import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Typography,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  LocalShipping as SupplierIcon,
  Newspaper as SchemeCardIcon,
  PendingActions as RoznamaIcon,
  ChevronLeft as ChevronLeftIcon,
  Book as BookIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  Assessment,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

const Sidebar = ({ isOpen = true, onToggle, mobileOpen = false, onMobileClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isCollapsed = !isOpen && !isMobile;

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);

  // Dialog state for restricted features
  const [restrictedDialog, setRestrictedDialog] = useState({
    open: false,
    featureName: "",
  });

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  // Transparent glass background
  const glassBackground = isDarkMode
    ? 'rgba(15, 23, 42, 0.4)'
    : 'rgba(255, 255, 255, 0.4)';

  const glassBackgroundStrong = isDarkMode
    ? 'rgba(15, 23, 42, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  const colors = {
    light: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(37, 99, 235, 0.1)',
      chip: {
        background: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.2)',
        text: '#2563EB'
      }
    },
    dark: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(148, 163, 184, 0.1)',
      chip: {
        background: 'rgba(96, 165, 250, 0.1)',
        border: 'rgba(96, 165, 250, 0.2)',
        text: '#60A5FA'
      }
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  // Handle restricted feature click
  const handleRestrictedFeatureClick = (featureName) => {
    setRestrictedDialog({
      open: true,
      featureName: featureName,
    });
  };

  // Close restricted dialog
  const handleCloseRestrictedDialog = () => {
    setRestrictedDialog({
      open: false,
      featureName: "",
    });
  };

  const mainMenuItems = [
    { label: "Dashboard", icon: DashboardIcon, path: "/", badge: null },
    {
      type: "dropdown",
      label: "Customers",
      icon: CustomersIcon,
      openState: customerOpen,
      setOpenState: setCustomerOpen,
      badge: "3",
      children: [
        { label: "View Customer", path: "/customers" },
        { label: "Sales", path: "/create-customer-transactions" },
        { label: "Sales History", path: "/customer-transactions" },
        { label: "Customer Ledger", path: "/customer-ledger", restricted: true },
        { label: "Advance Payments", path: "/advance-payment", restricted: true },
      ],
    },
    {
      type: "dropdown",
      label: "Suppliers",
      icon: SupplierIcon,
      openState: supplierOpen,
      setOpenState: setSupplierOpen,
      badge: "4",
      children: [
        { label: "View Supplier", path: "/supplier" },
        { label: "Add Supplier Transaction", path: "/supplier-transaction" },
        { label: "Supplier Transaction History", path: "/supplier-transaction-history" },
        { label: "Supplier Ledger", path: "/supplier-ledger", restricted: true },
      ],
    },
    {
      type: "dropdown",
      label: "Stock",
      icon: Assessment,
      openState: stockOpen,
      setOpenState: setStockOpen,
      badge: "4",
      children: [
        { label: "Add Stock", path: "/addItem" },
        { label: "View Inventory", path: "/inventory" },
        { label: "Stock Transaction Report", path: "/StockTransactionReport" },
        { label: "Stock Verification", path: "/stock-verification" },
      ],
    },
    { label: "Roznama", icon: RoznamaIcon, path: "/roznama", badge: null, restricted: true },
    { label: "Scheme Card", icon: SchemeCardIcon, path: "/scheme-card", badge: null, restricted: true },
    { label: "E-catalogue", icon: BookIcon, path: "/e-catalogue", badge: null, restricted: true },
  ];

  const isActive = (path) => activeItem === path;

  const handleNavigate = (path) => {
    navigate(path);
    setActiveItem(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleToggleCollapse = () => {
    if (onToggle) {
      onToggle();
    }
  };

  // Check if item is restricted and handle click
  const handleItemClick = (item) => {
    if (item.restricted) {
      handleRestrictedFeatureClick(item.label);
    } else {
      handleNavigate(item.path);
    }
  };

  const sidebarContent = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1.5,
          borderBottom: `1px solid ${currentColors.border}`,
        }}
      >
        {!isCollapsed && !isMobile && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              color: currentColors.textSecondary,
              opacity: 0.7,
            }}
          >
            Navigation
          </Typography>
        )}
        {!isMobile && (
          <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
            <IconButton
              size="small"
              onClick={handleToggleCollapse}
              sx={{
                marginLeft: isCollapsed ? 0 : "auto",
                borderRadius: 2,
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
                border: `1px solid ${currentColors.border}`,
                color: currentColors.textPrimary,
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                  transform: "scale(1.1)",
                  color: currentColors.primary,
                },
              }}
            >
              {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 20 }} /> : <ChevronLeftIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List sx={{ flex: 1, pt: 2.5 }}>
        {mainMenuItems.map((item, index) => {
          if (item.type === "dropdown") {
            const Icon = item.icon;
            const isActiveChild = item.children.some((child) => activeItem === child.path);
            const showCollapsed = isMobile ? false : isCollapsed;

            return (
              <React.Fragment key={index}>
                <Tooltip title={showCollapsed ? item.label : ""} placement="right" arrow>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => item.setOpenState(!item.openState)}
                      selected={isActiveChild}
                      sx={{
                        borderRadius: 2.5,
                        px: showCollapsed ? 1 : 1,
                        py: { xs: 1.2, sm: 1.5 },
                        minHeight: { xs: 44, sm: 52 },
                        backgroundColor: isActiveChild
                          ? isDarkMode ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isDarkMode
                            ? "rgba(96, 165, 250, 0.08)"
                            : "rgba(37, 99, 235, 0.05)",
                          transform: { xs: "none", sm: "translateX(4px)" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: showCollapsed ? 0 : { xs: 40, sm: 44 }, justifyContent: "center" }}>
                        <Icon sx={{ fontSize: { xs: 22, sm: 26 }, color: isActiveChild ? currentColors.primary : currentColors.textSecondary }} />
                      </ListItemIcon>
                      {!showCollapsed && !isMobile && (
                        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: isActiveChild ? 700 : 600, fontSize: { xs: "0.85rem", sm: "0.95rem" }, color: isActiveChild ? currentColors.primary : currentColors.textPrimary } }} />
                            {item.badge && <Chip label={item.badge} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: "0.6rem", sm: "0.7rem" }, fontWeight: 700, backgroundColor: currentColors.chip.background, color: isActiveChild ? currentColors.primary : currentColors.chip.text, border: `1px solid ${currentColors.chip.border}`, borderRadius: '8px' }} />}
                          </Box>
                          <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 20 }, transform: item.openState ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease", color: isActiveChild ? currentColors.primary : currentColors.textSecondary }} />
                        </Box>
                      )}
                      {isMobile && !showCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: isActiveChild ? 700 : 600, fontSize: "0.85rem", color: isActiveChild ? currentColors.primary : currentColors.textPrimary } }} />}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
                {(!showCollapsed || isMobile) && (
                  <Collapse in={item.openState} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: { xs: 1, sm: 2 }, pr: 1 }}>
                      {item.children.map((child) => {
                        const isRestricted = child.restricted === true;
                        return (
                          <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleItemClick(child)}
                              selected={isActive(child.path)}
                              sx={{
                                borderRadius: 2,
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1, sm: 1.2 },
                                minHeight: { xs: 38, sm: 42 },
                                opacity: isRestricted ? 0.7 : 1,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 }, justifyContent: "center" }}>
                                {isRestricted ? (
                                  <LockIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: isDarkMode ? '#94A3B8' : '#94A3B8' }} />
                                ) : (
                                  <CircleIcon sx={{ fontSize: { xs: 6, sm: 8 }, color: isActive(child.path) ? currentColors.primary : currentColors.textSecondary }} />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  sx: {
                                    fontWeight: isActive(child.path) ? 600 : 500,
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    color: isRestricted
                                      ? (isDarkMode ? '#64748B' : '#94A3B8')
                                      : (isActive(child.path) ? currentColors.primary : currentColors.textSecondary),
                                    textDecoration: isRestricted ? 'line-through' : 'none',
                                  }
                                }}
                              />
                              {isRestricted && (
                                <Chip
                                  label="PRO"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: '0.5rem',
                                    fontWeight: 700,
                                    backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.15)',
                                    color: '#F59E0B',
                                    border: '1px solid rgba(251, 191, 36, 0.3)',
                                    ml: 1,
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          } else {
            const Icon = item.icon;
            const active = isActive(item.path);
            const showCollapsed = isMobile ? false : isCollapsed;
            const isRestricted = item.restricted === true;

            return (
              <Tooltip key={item.path} title={showCollapsed ? item.label : ""} placement="right" arrow>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleItemClick(item)}
                    selected={active}
                    sx={{
                      borderRadius: 2.5,
                      px: showCollapsed ? 1 : 1,
                      py: { xs: 1.2, sm: 1.5 },
                      minHeight: { xs: 44, sm: 52 },
                      backgroundColor: active ? (isDarkMode ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)") : "transparent",
                      opacity: isRestricted ? 0.7 : 1,
                      "&:hover": {
                        backgroundColor: isRestricted
                          ? "transparent"
                          : (isDarkMode ? "rgba(96, 165, 250, 0.08)" : "rgba(37, 99, 235, 0.05)"),
                        transform: isRestricted ? "none" : { xs: "none", sm: "translateX(4px)" }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: showCollapsed ? 0 : { xs: 40, sm: 44 }, justifyContent: "center" }}>
                      {isRestricted ? (
                        <LockIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: isDarkMode ? '#64748B' : '#94A3B8' }} />
                      ) : (
                        <Icon sx={{ fontSize: { xs: 20, sm: 22 }, color: active ? currentColors.primary : currentColors.textSecondary }} />
                      )}
                    </ListItemIcon>
                    {!showCollapsed && !isMobile && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              fontWeight: active ? 700 : 600,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              color: isRestricted
                                ? (isDarkMode ? '#64748B' : '#94A3B8')
                                : (active ? currentColors.primary : currentColors.textPrimary),
                              textDecoration: isRestricted ? 'line-through' : 'none',
                            }
                          }}
                        />
                        {isRestricted && (
                          <Chip
                            label="PRO"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.5rem',
                              fontWeight: 700,
                              backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.15)',
                              color: '#F59E0B',
                              border: '1px solid rgba(251, 191, 36, 0.3)',
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {isMobile && !showCollapsed && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              fontWeight: active ? 700 : 600,
                              fontSize: "0.85rem",
                              color: isRestricted
                                ? (isDarkMode ? '#64748B' : '#94A3B8')
                                : (active ? currentColors.primary : currentColors.textPrimary),
                              textDecoration: isRestricted ? 'line-through' : 'none',
                            }
                          }}
                        />
                        {isRestricted && (
                          <Chip
                            label="PRO"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.45rem',
                              fontWeight: 700,
                              backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.15)',
                              color: '#F59E0B',
                              border: '1px solid rgba(251, 191, 36, 0.3)',
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {showCollapsed && active && !isRestricted && (
                      <Box sx={{ position: "absolute", right: 12, width: { xs: 4, sm: 6 }, height: { xs: 4, sm: 6 }, borderRadius: "50%", background: currentColors.primary, boxShadow: `0 0 8px ${currentColors.primary}40` }} />
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          }
        })}
      </List>

      {(!isCollapsed || isMobile) && (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderTop: `1px solid ${currentColors.border}`, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "0.9rem", sm: "1rem" }, mb: 0.5, color: currentColors.primary }}>JewelStock</Typography>
          <Typography variant="caption" sx={{ color: currentColors.textSecondary, display: "block", fontSize: { xs: "0.6rem", sm: "0.7rem" }, fontWeight: 500 }}>Version 1.0.0</Typography>
        </Box>
      )}
    </>
  );

  // For mobile - show drawer when mobileOpen is true
  if (isMobile) {
    return (
      <>
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={onMobileClose}
          variant="temporary"
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              background: glassBackgroundStrong,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderRight: `1px solid ${currentColors.border}`,
              boxShadow: "none",
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        {/* Restricted Feature Dialog */}
        <Dialog
          open={restrictedDialog.open}
          onClose={handleCloseRestrictedDialog}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              maxWidth: 420,
              width: '100%',
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pb: 1,
            borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <LockIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#F1F5F9' : '#0F172A' }}>
                Premium Feature
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}>
                {restrictedDialog.featureName}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              background: isDarkMode
                ? 'rgba(251, 191, 36, 0.1)'
                : 'rgba(251, 191, 36, 0.08)',
              border: `1px solid ${isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.15)'}`,
              mb: 2,
            }}>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#FBBF24' : '#D97706', fontWeight: 600, mb: 1 }}>
                ⚠️ Feature Not Available
              </Typography>
              <DialogContentText sx={{ color: isDarkMode ? '#CBD5E1' : '#475569', fontSize: '0.95rem' }}>
                The <strong>"{restrictedDialog.featureName}"</strong> feature is not included in your current subscription plan.
              </DialogContentText>
            </Box>

            <Typography variant="body2" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mb: 1 }}>
              To access this feature, please:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, color: isDarkMode ? '#CBD5E1' : '#475569' }}>
              <li>Upgrade your subscription plan</li>
              <li>Contact your account administrator</li>
              <li>Reach out to support for assistance</li>
            </Box>

            <Box sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              background: isDarkMode
                ? 'rgba(59, 130, 246, 0.1)'
                : 'rgba(59, 130, 246, 0.08)',
              border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)'}`,
            }}>
              {/* <Typography variant="caption" sx={{ color: isDarkMode ? '#60A5FA' : '#2563EB', display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>📧</span> Contact: <strong>support@jewelstock.com</strong>
              </Typography> */}
              <Typography variant="caption" sx={{ color: isDarkMode ? '#60A5FA' : '#2563EB', display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <span>📞</span> Phone: <strong>+91 99747 06814</strong>
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
            <Button
              onClick={handleCloseRestrictedDialog}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                py: 1.2,
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1D4ED8, #1E40AF)',
                },
              }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // For desktop - use permanent drawer
  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            boxSizing: "border-box",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflowX: "hidden",
            background: glassBackground,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRight: `1px solid ${currentColors.border}`,
            boxShadow: "none",
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Restricted Feature Dialog - Desktop */}
      <Dialog
        open={restrictedDialog.open}
        onClose={handleCloseRestrictedDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            maxWidth: 420,
            width: '100%',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 1,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <LockIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#F1F5F9' : '#0F172A' }}>
              Premium Feature
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}>
              {restrictedDialog.featureName}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{
            p: 2,
            borderRadius: 2,
            background: isDarkMode
              ? 'rgba(251, 191, 36, 0.1)'
              : 'rgba(251, 191, 36, 0.08)',
            border: `1px solid ${isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.15)'}`,
            mb: 2,
          }}>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#FBBF24' : '#D97706', fontWeight: 600, mb: 1 }}>
              ⚠️ Feature Not Available
            </Typography>
            <DialogContentText sx={{ color: isDarkMode ? '#CBD5E1' : '#475569', fontSize: '0.95rem' }}>
              The <strong>"{restrictedDialog.featureName}"</strong> feature is not included in your current subscription plan.
            </DialogContentText>
          </Box>

          <Typography variant="body2" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mb: 1 }}>
            To access this feature, please:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, color: isDarkMode ? '#CBD5E1' : '#475569' }}>
            <li>Upgrade your subscription plan</li>
            <li>Contact your account administrator</li>
            <li>Reach out to support for assistance</li>
          </Box>

          <Box sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: isDarkMode
              ? 'rgba(59, 130, 246, 0.1)'
              : 'rgba(59, 130, 246, 0.08)',
            border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)'}`,
          }}>
            {/* <Typography variant="caption" sx={{ color: isDarkMode ? '#60A5FA' : '#2563EB', display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📧</span> Contact: <strong>support@jewelstock.com</strong>
            </Typography> */}
            <Typography variant="caption" sx={{ color: isDarkMode ? '#60A5FA' : '#2563EB', display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <span>📞</span> Phone: <strong>+91 99747 06814</strong>
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
          <Button
            onClick={handleCloseRestrictedDialog}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
              py: 1.2,
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #1D4ED8, #1E40AF)',
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;